// Register page
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, TextField, Button, Alert, CircularProgress, MenuItem } from "@mui/material";
import { UserPlus } from "lucide-react";
import { useAuth } from "../components/AuthContext";
import { formatApiError } from "../api/api";

const ROLES = [
  { value: "credit_officer", label: "Credit Officer" },
  { value: "risk_analyst", label: "Risk Analyst" },
  { value: "branch_manager", label: "Branch Manager" },
  { value: "admin", label: "Admin / Chief Risk Officer" },
];

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", title: "", email: "", password: "", role: "credit_officer" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <Box className="auth-bg" sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3, position: "relative" }}>
      <div className="auth-watermark" />
      <Box component="form" onSubmit={submit} sx={{ width: "100%", maxWidth: 460, position: "relative", zIndex: 1, background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 1, p: 4 }}>
        <Box className="cb-display" sx={{ fontSize: 26, fontWeight: 600, color: "#111827", mb: 0.4 }}>Request access</Box>
        <Box sx={{ fontSize: 13, color: "#6B7280", mb: 2.4 }}>Create a new ClimateLedger workspace user.</Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="register-error">{error}</Alert>}

        <Box sx={{ display: "grid", gap: 1.4 }}>
          <TextField required size="small" label="Full name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            inputProps={{ "data-testid": "reg-name" }} />
          <TextField size="small" label="Title (e.g., Senior Credit Officer)" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            inputProps={{ "data-testid": "reg-title" }} />
          <TextField required size="small" type="email" label="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            inputProps={{ "data-testid": "reg-email" }} />
          <TextField required size="small" type="password" label="Password (min 8 chars)" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            inputProps={{ "data-testid": "reg-password" }} />
          <TextField select size="small" label="Role" value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            SelectProps={{ inputProps: { "data-testid": "reg-role" } }}>
            {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>
        </Box>

        <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}
          startIcon={loading ? <CircularProgress size={14} sx={{ color: "#FFF" }} /> : <UserPlus size={15} />}
          data-testid="register-submit-btn"
          sx={{ mt: 2.2, background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
          {loading ? "Creating account…" : "Create account"}
        </Button>

        <Box sx={{ mt: 2, fontSize: 12, textAlign: "center", color: "#6B7280" }}>
          Already have access? <Link to="/login" style={{ color: "#2E5A35", fontWeight: 600, textDecoration: "none" }}>Sign in →</Link>
        </Box>
      </Box>
    </Box>
  );
}
